/* eslint-disable react/jsx-no-undef */
import React, { ForwardRefExoticComponent, RefAttributes, useContext, useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import PagedResponse from "../../../api/models/PagedResponse";
import { CustomerPart } from "../../../api/models/substrate/CustomerPart";
import TesaTmbApi from "../../../api/services/TesaTmbApi";
import CustomSearchAutoComplete, {
  CustomSearchAutocompleteProps,
} from "../../../components/Autocomplete/CustomSearchAutoComplete";
import CustomButton from "../../../components/Button/CustomButton";
import { ConfirmationDialogContext } from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";

type CustomerPartCreateForm = {
  customerPart: CustomerPart | null;
};

const TEXT_CREATE_NEW = "You are about to create a new type of customer part.";
const TEXT_SELECT_PREV =
  "You are about to create a new type of customer part, but similar types are available in the database. Select an existing one from the drop down or create a new type.";

const CustomerPartSearchAutocomplete = CustomSearchAutoComplete as ForwardRefExoticComponent<
  CustomSearchAutocompleteProps<CustomerPart> & RefAttributes<{ reset: () => void }>
>;

interface CustomerPartCreateDialogProps {
  open: boolean;
  initialValue: CustomerPart | null;
  onConfirm: (value: CustomerPart) => void;
  onClose: () => void;
}

const CustomerPartCreateDialog: React.FC<CustomerPartCreateDialogProps> = (props) => {
  const { open, initialValue, onClose, onConfirm } = props;
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<CustomerPart[]>([]);
  const [hasSuggestions, setHasSuggestions] = useState(false);
  const dialogContext = useContext(ConfirmationDialogContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const tmbApi = new TesaTmbApi(dialogContext);

  const yupValidationSchema = Yup.object().shape({
    customerPart: Yup.object()
      .shape({ id: Yup.string().nullable(), name: Yup.string().required() })
      .nullable()
      .required("Please select an option or enter a new name"),
  });

  useEffect(() => {
    if (open && initialValue) {
      setLoading(true);
      tmbApi
        .fetchCustomerPartTypes(initialValue.name)
        .then((res) => {
          setInitialData(res.data.data);
          setHasSuggestions(!!res.data.data.length);
        })
        .catch(handleHttpError)
        .finally(() => setLoading(false));
    }
  }, [open, initialValue]);

  return (
    <CustomDialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      <h2>ARE YOU SURE?</h2>
      <p style={{ height: "2.5rem" }}>{hasSuggestions ? TEXT_SELECT_PREV : TEXT_CREATE_NEW}</p>
      <Formik
        initialValues={{ customerPart: initialValue }}
        onSubmit={(newVal) => {
          if (newVal.customerPart) {
            onConfirm(newVal.customerPart);
          }
        }}
        validationSchema={yupValidationSchema}
      >
        {(formikProps: FormikProps<CustomerPartCreateForm>): JSX.Element => {
          const { errors, isValid, setFieldValue, touched, values } = formikProps;

          return (
            <Form>
              <Grid item xs={12}>
                <Box pb={4}>
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <CustomerPartSearchAutocomplete
                      autoFocus
                      error={!!(errors.customerPart && touched.customerPart)}
                      freeSolo
                      helperText={[errors.customerPart && touched.customerPart ? errors.customerPart : ""].join(" ")}
                      inputLabel="Type of Customer Part"
                      initialData={initialData}
                      mandatory
                      openOnFocus={!!initialData.length}
                      onChange={(e, value): void => {
                        setFieldValue("customerPart", value);
                      }}
                      optionFetch={(term): Promise<PagedResponse<CustomerPart>> =>
                        tmbApi.fetchCustomerPartTypes(term).then((res) => {
                          setHasSuggestions(!!res.data.data.length);
                          return res.data;
                        })
                      }
                      map={(value): { key: string; label: string } => ({
                        key: `${value.id || value.name}`,
                        label: value.name,
                      })}
                      mapToData={(name) => ({
                        id: null,
                        name,
                      })}
                      value={values.customerPart}
                    />
                  )}
                </Box>
              </Grid>
              <Grid container direction="row" justify="flex-end">
                <CustomButton
                  disabled={!isValid}
                  type="submit"
                  primary
                  text={values.customerPart?.id ? "Use" : "Create"}
                />
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default CustomerPartCreateDialog;
